import { Alert, Button, FloatingLabel, Form, Modal } from 'react-bootstrap'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import {
  GetCollectionSlotDocument,
  GetCollectionSlotQuery,
  GetCollectionSlotQueryVariables,
  useInsertCollectionSlotMutation,
  useUpdateCollectionSlotMutation,
} from '../generated/urql.administrator'
import { useForm } from 'react-hook-form'
import { useClient } from 'urql'
import moment from 'moment/moment'

export type CollectionSlotModalType = {
  show: (args: { id?: number; collectionPointId: number }) => void
}

type FormCollectionSlot = {
  dateFrom: string
  dateTo: string
}

const CollectionSlotModal = forwardRef<
  CollectionSlotModalType,
  {
    onChange: () => void
  }
>(({ onChange }, ref) => {
  const [show, setShow] = useState(false)
  const [id, setId] = useState<number>()
  const [collectionPointId, setCollectionPointId] = useState<number>()
  const client = useClient()
  const [, setGetCollectionSlotFetching] = useState(false)
  const [{ fetching: updateCollectionSlotFetching }, updateCollectionSlot] =
    useUpdateCollectionSlotMutation()
  const [{ fetching: insertCollectionSlotFetching }, insertCollectionSlot] =
    useInsertCollectionSlotMutation()
  const [generalError, setGeneralError] = useState<string>()
  const {
    handleSubmit,
    reset,
    formState: { errors },
    register,
  } = useForm<FormCollectionSlot>({
    defaultValues: {
      dateFrom: undefined,
      dateTo: undefined,
    },
  })
  useImperativeHandle(ref, () => ({
    show: ({ id, collectionPointId }) => {
      setId(id)
      setCollectionPointId(collectionPointId)
      reset({
        dateFrom: undefined,
        dateTo: undefined,
      })
      setShow(true)
    },
  }))

  useEffect(() => {
    if (id) {
      setGetCollectionSlotFetching(true)
      client
        .query<GetCollectionSlotQuery, GetCollectionSlotQueryVariables>(
          GetCollectionSlotDocument,
          {
            id,
          }
        )
        .toPromise()
        .then(({ data }) => {
          if (data?.collection_slot_by_pk) {
            reset({
              dateFrom: data.collection_slot_by_pk.dateFrom
                ? moment(data.collection_slot_by_pk.dateFrom).format(
                    'yyyy-MM-DDTHH:mm'
                  )
                : '',
              dateTo: data.collection_slot_by_pk.dateTo
                ? moment(data.collection_slot_by_pk.dateTo).format(
                    'yyyy-MM-DDTHH:mm'
                  )
                : '',
            })
          }
        })
        .finally(() => setGetCollectionSlotFetching(false))
    }
  }, [id, client, reset])

  async function doSaveCollectionSlot(
    collectionSlot: FormCollectionSlot
  ) {
    setGeneralError(undefined)

    if (!collectionPointId) {
      setGeneralError('Missing collectionPointId')
      return
    }

    const { error } = id
      ? await updateCollectionSlot({
          ...collectionSlot,
          dateFrom: moment(collectionSlot.dateFrom).toISOString(),
          dateTo: moment(collectionSlot.dateTo).toISOString(),
          collectionPointId,
          id,
        })
      : await insertCollectionSlot({
          ...collectionSlot,
          dateFrom: moment(collectionSlot.dateFrom).toISOString(),
          dateTo: moment(collectionSlot.dateTo).toISOString(),
          collectionPointId,
        })

    if (error) {
      const code = error.graphQLErrors.find(
        (e) => e.extensions.code === 'constraint-violation'
      )
      const message = code
        ? 'Zablokowano aby zapobiec duplikatom terminów'
        : error.message || 'Coś poszło nie tak'
      setGeneralError(message)
      return
    }

    setShow(false)
    setId(undefined)
    setCollectionPointId(undefined)
    onChange()
  }

  return (
    <Modal
      show={show}
      onHide={() => {
        setId(undefined)
        setCollectionPointId(undefined)
        setShow(false)
      }}
      size="lg"
    >
      <Form onSubmit={handleSubmit(doSaveCollectionSlot)}>
        <Modal.Header closeButton>
          <Modal.Title>Slot pobrania</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {generalError && <Alert variant="danger">{generalError}</Alert>}

          <FloatingLabel label="Data/czas od" className="mb-3">
            <Form.Control
              isInvalid={!!errors.dateFrom}
              type="datetime-local"
              placeholder="Od"
              {...register('dateFrom', {
                required: 'Proszę podać datę początkową',
              })}
            />
            {errors.dateFrom && (
              <Form.Control.Feedback>
                {errors.dateFrom.message}
              </Form.Control.Feedback>
            )}
          </FloatingLabel>

          <FloatingLabel label="Data/czas do" className="mb-3">
            <Form.Control
              isInvalid={!!errors.dateTo}
              type="datetime-local"
              placeholder="Do"
              {...register('dateTo', {
                required: 'Proszę podać datę końcową',
              })}
            />
            {errors.dateTo && (
              <Form.Control.Feedback>
                {errors.dateTo.message}
              </Form.Control.Feedback>
            )}
          </FloatingLabel>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="success"
            type="submit"
            disabled={
              updateCollectionSlotFetching || insertCollectionSlotFetching
            }
          >
            Zapisz
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
})

export default CollectionSlotModal

